import type { PageConstructorData } from "~/core/services/page-constructor.service";

type ListItem = {
  "@type": "ListItem";
  position: number;
  name: string;
  item?: string;
};

export type BreadcrumbList = {
  "@context": "https://schema.org";
  "@type": "BreadcrumbList";
  inLanguage?: string;
  itemListElement: Array<ListItem>;
};

type BuildBreadcrumbListInput = {
  data: PageConstructorData;
  homeLabel: string | null | undefined;
  originUrl: string;
  lang: string;
  // Skip the "page contains a breadcrumbs block in its content" check.
  // Set on routes that render <PageBreadcrumbs> outside of PageConstructor.
  forceVisible?: boolean;
};

const hasVisibleBreadcrumbsBlock = (data: PageConstructorData): boolean => {
  const components = data.page?.translations?.[0]?.data ?? [];
  return components.some((component) => {
    const item = component?.item;
    return (
      typeof item === "object" &&
      item !== null &&
      "__typename" in item &&
      (item as { __typename?: unknown }).__typename === "breadcrumbs"
    );
  });
};

const buildAbsoluteUrl = (origin: string, segments: Array<string>): string => {
  const path = segments.filter(Boolean).map(encodeURIComponent).join("/");
  return `${origin}/${path}`;
};

const safeOrigin = (url: string): string | null => {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
};

export const buildBreadcrumbList = ({
  data,
  homeLabel,
  originUrl,
  lang,
  forceVisible,
}: BuildBreadcrumbListInput): BreadcrumbList | null => {
  if (!forceVisible && !hasVisibleBreadcrumbsBlock(data)) {
    return null;
  }

  const origin = safeOrigin(originUrl);
  if (!origin) {
    return null;
  }

  const segment = data.page?.segment ?? "";
  const slug = data.page?.slug ?? "";
  const segmentLabel =
    data.parentPage?.preview_translations?.[0]?.title ?? (segment || null);
  const slugLabel =
    data.page?.preview_translations?.[0]?.title ?? (slug || null);

  const items: Array<ListItem> = [];

  if (homeLabel) {
    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name: homeLabel,
      item: buildAbsoluteUrl(origin, [lang]),
    });
  }

  if (segment && segmentLabel) {
    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name: segmentLabel,
      item: buildAbsoluteUrl(origin, [lang, segment]),
    });
  }

  if (slug && slugLabel) {
    items.push({
      "@type": "ListItem",
      position: items.length + 1,
      name: slugLabel,
    });
  }

  // Google requires at least two ListItem entries for a valid BreadcrumbList.
  if (items.length < 2) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    ...(lang ? { inLanguage: lang } : {}),
    itemListElement: items,
  };
};

const isBreadcrumbListNode = (node: unknown): boolean => {
  if (!node || typeof node !== "object") return false;
  const type = (node as { "@type"?: unknown })["@type"];
  return (
    type === "BreadcrumbList" ||
    (Array.isArray(type) && type.includes("BreadcrumbList"))
  );
};

export const containsBreadcrumbList = (jsonld: unknown): boolean => {
  if (!jsonld) return false;

  let parsed: unknown = jsonld;
  if (typeof jsonld === "string") {
    try {
      parsed = JSON.parse(jsonld);
    } catch {
      return false;
    }
  }

  if (Array.isArray(parsed)) {
    return parsed.some(isBreadcrumbListNode);
  }

  if (parsed && typeof parsed === "object") {
    if (isBreadcrumbListNode(parsed)) return true;
    const graph = (parsed as { "@graph"?: unknown })["@graph"];
    if (Array.isArray(graph)) return graph.some(isBreadcrumbListNode);
  }

  return false;
};

type RootLoaderShape = {
  translations?: {
    static_translations?: {
      breadcrumbs?: Array<{ home_label?: string | null } | null> | null;
    } | null;
  } | null;
};

type MetaMatchEntry = { id: string; data?: unknown };

export const getBreadcrumbsHomeLabel = (
  matches: ReadonlyArray<MetaMatchEntry>,
): string | null => {
  const rootData = matches.find((m) => m.id === "root")?.data as
    | RootLoaderShape
    | undefined;
  return (
    rootData?.translations?.static_translations?.breadcrumbs?.[0]?.home_label ??
    null
  );
};
